import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Download, Printer, ExternalLink, Package, ChevronRight, FileText } from "lucide-react";
import { format } from "date-fns";

interface LabelRecord {
  id: string;
  box_number: string;
  name: string;
  shipping_label_url: string;
  tracking_number: string | null;
  label_generated_at: string;
  easypost_tracking_url: string | null;
}

interface ShippingLabelsHistoryProps {
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ShippingLabelsHistory({ isOpen: controlledOpen, onOpenChange }: ShippingLabelsHistoryProps = {}) {
  const [labels, setLabels] = useState<LabelRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setIsOpen = onOpenChange || setInternalOpen;

  useEffect(() => {
    if (isOpen) {
      fetchLabels();
    }
  }, [isOpen]);

  const fetchLabels = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('boxes')
        .select('id, box_number, name, shipping_label_url, tracking_number, label_generated_at, easypost_tracking_url')
        .eq('user_id', user.id)
        .not('shipping_label_url', 'is', null)
        .order('label_generated_at', { ascending: false });

      if (error) throw error;
      setLabels(data || []);
    } catch (err) {
      console.error('Error fetching labels:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (labelUrl: string) => {
    const printWindow = window.open(labelUrl, '_blank');
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  const handleDownload = async (labelUrl: string, boxNumber: string) => {
    try {
      const response = await fetch(labelUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `shipping-label-${boxNumber}.png`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      // Fallback: open in new tab
      window.open(labelUrl, '_blank');
    }
  };

  // Only render trigger button if not controlled externally
  const isControlled = controlledOpen !== undefined;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            <FileText className="h-4 w-4" />
            <span className="hidden sm:inline">Shipping Labels</span>
            <span className="sm:hidden">Labels</span>
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Printer className="h-5 w-5" />
            Shipping Labels History
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading labels...</p>
          </div>
        ) : labels.length === 0 ? (
          <div className="py-12 text-center">
            <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="font-semibold mb-2">No shipping labels yet</h3>
            <p className="text-muted-foreground text-sm">
              Labels will appear here after you schedule a pickup
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {labels.map((label) => (
              <Card key={label.id} className="overflow-hidden">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium truncate">{label.name}</h4>
                        <Badge variant="secondary" className="text-xs shrink-0">
                          {label.box_number}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        Generated {format(new Date(label.label_generated_at), "MMM d, yyyy")}
                      </p>
                      {label.tracking_number && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-muted-foreground">Tracking:</span>
                          {label.easypost_tracking_url ? (
                            <a
                              href={label.easypost_tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline flex items-center gap-1"
                            >
                              {label.tracking_number}
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          ) : (
                            <span className="font-mono">{label.tracking_number}</span>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(label.shipping_label_url, label.box_number)}
                        title="Download label"
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        onClick={() => handlePrint(label.shipping_label_url)}
                        title="Print label"
                      >
                        <Printer className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
